import { getAccessToken, refreshAccessToken } from "./googleAuth";

const DRIVE = "https://www.googleapis.com/drive/v3";
const UPLOAD = "https://www.googleapis.com/upload/drive/v3";

export class DriveError extends Error {
  constructor(status, body) {
    super(`Drive ${status}${body ? `: ${body}` : ""}`);
    this.name = "DriveError";
    this.status = status;
  }
}

const toEpoch = (rfc3339) => {
  const t = rfc3339 ? new Date(rfc3339).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
};

/**
 * Build a raw Google Drive appDataFolder adapter. This layer ONLY talks to the
 * REST API: it checks `res.ok`, throws on failure (so a failed upload can never
 * masquerade as success), transparently refreshes the OAuth token on a 401 and
 * retries once, and normalises `modifiedTime`/`createdTime` to epoch-ms numbers.
 *
 * Higher-level concerns (file-id caching, duplicate-file reconciliation,
 * read-merge-write, dirty tracking, watermarks) live in syncEngine.js so they
 * can be unit-tested against an in-memory fake that implements this same shape.
 *
 * Interface (also implemented by the test FakeDrive):
 *   listByName(name)            -> Promise<[{id,name,modifiedTime,createdTime}]>  newest first, no trashed
 *   listByPrefix(prefix)        -> Promise<[{id,name,modifiedTime,createdTime}]>
 *   getContent(id)              -> Promise<any|null>      (null when missing)
 *   getMeta(id)                 -> Promise<{id,modifiedTime}|null>
 *   create(name, contentString) -> Promise<{id,modifiedTime}>
 *   update(id, contentString)   -> Promise<{id,modifiedTime}>   (throws DriveError(404) if gone)
 *   remove(id)                  -> Promise<void>
 */
export const createDriveClient = ({
  getToken = getAccessToken,
  refreshToken = refreshAccessToken,
  fetchImpl = (...a) => fetch(...a),
} = {}) => {
  // Centralised fetch with 401→refresh→retry-once and loud error propagation.
  const authedFetch = async (url, options = {}, { allow404 = false } = {}) => {
    const token = getToken();
    if (!token) throw new DriveError(401, "no access token");

    const send = (tok) =>
      fetchImpl(url, {
        ...options,
        headers: { ...(options.headers || {}), Authorization: `Bearer ${tok}` },
      });

    let res = await send(token);

    if (res.status === 401 && refreshToken) {
      const fresh = await refreshToken();
      if (fresh) res = await send(fresh);
    }

    if (res.status === 404 && allow404) return res;
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      throw new DriveError(res.status, body);
    }
    return res;
  };

  const listQuery = async (q) => {
    const res = await authedFetch(
      `${DRIVE}/files?spaces=appDataFolder&fields=files(id,name,modifiedTime,createdTime)` +
        `&q=${encodeURIComponent(q)}&orderBy=modifiedTime desc&pageSize=1000`,
      { method: "GET" }
    );
    const { files } = await res.json();
    return (files || []).map((f) => ({
      id: f.id,
      name: f.name,
      modifiedTime: toEpoch(f.modifiedTime),
      createdTime: toEpoch(f.createdTime),
    }));
  };

  const listByName = (name) =>
    listQuery(`name = '${name.replace(/'/g, "\\'")}' and trashed = false`);

  const listByPrefix = (prefix) =>
    listQuery(`name contains '${prefix.replace(/'/g, "\\'")}' and trashed = false`);

  const getContent = async (id) => {
    const res = await authedFetch(`${DRIVE}/files/${id}?alt=media`, { method: "GET" }, { allow404: true });
    if (res.status === 404) return null;
    return res.json();
  };

  const getMeta = async (id) => {
    const res = await authedFetch(
      `${DRIVE}/files/${id}?fields=id,modifiedTime`,
      { method: "GET" },
      { allow404: true }
    );
    if (res.status === 404) return null;
    const f = await res.json();
    return { id: f.id, modifiedTime: toEpoch(f.modifiedTime) };
  };

  const create = async (name, contentString) => {
    const metadata = { name, parents: ["appDataFolder"], mimeType: "application/json" };
    const boundary = "hanswehr_boundary";
    const body = [
      `--${boundary}`,
      "Content-Type: application/json; charset=UTF-8",
      "",
      JSON.stringify(metadata),
      `--${boundary}`,
      "Content-Type: application/json",
      "",
      contentString,
      `--${boundary}--`,
    ].join("\r\n");

    const res = await authedFetch(`${UPLOAD}/files?uploadType=multipart&fields=id,modifiedTime`, {
      method: "POST",
      headers: { "Content-Type": `multipart/related; boundary=${boundary}` },
      body,
    });
    const created = await res.json();
    if (!created?.id) throw new DriveError(500, "create returned no id");
    return { id: created.id, modifiedTime: toEpoch(created.modifiedTime) };
  };

  const update = async (id, contentString) => {
    const res = await authedFetch(
      `${UPLOAD}/files/${id}?uploadType=media&fields=id,modifiedTime`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: contentString,
      },
      { allow404: true }
    );
    if (res.status === 404) throw new DriveError(404, "file gone");
    const f = await res.json();
    return { id: f.id, modifiedTime: toEpoch(f.modifiedTime) };
  };

  const remove = async (id) => {
    await authedFetch(`${DRIVE}/files/${id}`, { method: "DELETE" }, { allow404: true });
  };

  return { listByName, listByPrefix, getContent, getMeta, create, update, remove };
};

// Default adapter wired to the real OAuth token + silent refresh.
export const driveClient = createDriveClient();
